import { isRoleCurrent } from "../../../pages/Dashboard/EmpDashboard/employeeListUtils";

/**
 * Who an employee currently reports to, read from `/fetch-employees`.
 *
 * A coach may only apply for leave once a manager is assigned to them. The
 * backend does not enforce this — without a manager only an executive could
 * act on the request — so the profile does.
 *
 * `/fetch-employees` returns manager links unfiltered: removing a manager only
 * sets the link's `active_to`, and ended or future links come back looking
 * like live ones (`.md/COACH_MANAGER_LINKS.md`). So a link counts only while
 * its assignment window is open and the manager's own role is still current,
 * and only on one of the employee's own current roles.
 */

function inWindow(from, to, now) {
  const start = from ? new Date(from) : null;
  const end = to ? new Date(to) : null;
  // An unparseable bound reads as unbounded, as in `isRoleCurrent`.
  if (start && !Number.isNaN(start.getTime()) && start > now) return false;
  if (end && !Number.isNaN(end.getTime()) && end < now) return false;
  return true;
}

/** Whether one `managers[]` entry is a live reporting link right now. */
export function isManagerLinkLive(manager, now = new Date()) {
  if (!manager) return false;
  return (
    inWindow(manager.manager_assignment_active_from, manager.manager_assignment_active_to, now) &&
    inWindow(null, manager.active_to, now)
  );
}

/**
 * Names of the employee's current managers, de-duplicated.
 *
 * @param {Array<Object>} employees - `/fetch-employees` rows.
 * @param {string} employeeId - The employee's Employees id.
 * @param {Date} [now]
 * @returns {Array<string>} Empty when no live manager is assigned.
 */
export function currentManagerNames(employees, employeeId, now = new Date()) {
  if (!employeeId || !Array.isArray(employees)) return [];
  const self = employees.find((e) => String(e?.employee_id) === String(employeeId));
  const names = new Set();
  (Array.isArray(self?.roles) ? self.roles : [])
    .filter((entry) => isRoleCurrent(entry, now))
    .forEach((entry) => {
      (Array.isArray(entry?.managers) ? entry.managers : [])
        .filter((m) => isManagerLinkLive(m, now))
        .forEach((m) => {
          const name = `${m.first_name || ""} ${m.last_name || ""}`.trim();
          names.add(name || "your manager");
        });
    });
  return [...names];
}
